import React, { useEffect, useState, useRef, useCallback } from "react";
import { Toolbar, Box, AppBar, Typography, Tooltip, Avatar, IconButton, useMediaQuery, useTheme, Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import { useTranslation } from "react-i18next";
import Notifications from "./Notifications";
import { useThemeMode } from "../../Styles/ThemeModeContext";

function Header(props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mode, toggleMode } = useThemeMode();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const clickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleAvatarClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    if (clickTimer.current) {
      clearTimeout(clickTimer.current);
      clickTimer.current = null;
      // Double click — navigate to profile
      window.location.href = `/profile/${props.state.user?.aliasName || props.state.user?.id}`;
    } else {
      // Single click — open menu after short delay
      const target = e.currentTarget;
      clickTimer.current = setTimeout(() => {
        clickTimer.current = null;
        setMenuAnchor(target);
      }, 250);
    }
  }, [props.state.user]);
  useEffect(() => {
    if (props.state.isMobile === undefined || props.state.isMobile !== isMobile) {
      props.basicFunctions.updateState({ isMobile });
    }
  }, [isMobile, props.state.isMobile, props.basicFunctions]); // Dependencies for the effect

  return (
    <AppBar
      id="header"
      position="static"
      sx={{
        top: 0,
        padding: "12px 0",
        backgroundColor: "var(--surface-1)",
        color: "var(--text-primary)",
        height: "auto",
        display: "flex",
        zIndex: 1000,
        overflow: "hidden",
        boxShadow: "none",
        borderBottom: "1px solid var(--border-soft)",
      }}
    >
      <Toolbar
        id="header-toolbar"
        disableGutters
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 3,
          padding: isMobile ? "0 16px" : "0 24px",
          width: "100%",
          boxSizing: "border-box",
          margin: "0 auto",
        }}
      >
        <Box
          id="header-left"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: isMobile ? 2 : 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 0.5,
            }}
          >
            <Box
              component="a"
              href="/"
              sx={{
                textDecoration: "none",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography
                id="siteNameType"
                sx={{ color: "var(--text-primary)", fontWeight: 700, letterSpacing: 0.5, lineHeight: 1.2 }}
                variant={isMobile ? "h5" : "h4"}
              >
                {t("common.siteName")}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: "var(--text-secondary)",
              }}
            >
              {t("common.poweredBy")} {" "}
              <Box
                component="a"
                target="_blank"
                href="https://www.musicrandomizer.com"
                sx={{
                  color: "var(--accent)",
                  fontWeight: 600,
                  textDecoration: "underline",
                }}
              >
                MusicRandomizer.com
              </Box>
            </Typography>
          </Box>
          <Box
            component="a"
            href="/Community"
            sx={{
              color: "var(--text-secondary)",
              fontWeight: 600,
              fontSize: "0.95rem",
              letterSpacing: 0.2,
              textDecoration: "none",
              transition: "color 0.2s ease, transform 0.2s ease",
              "&:hover": {
                color: "var(--text-primary)",
                transform: "translateY(-1px)",
              },
            }}
          >
            {t("navigation.community")}
          </Box>
        </Box>

        <Box
          id="header-right"
          sx={{
            display: "flex",
            gap: isMobile ? 1.5 : 3,
            alignItems: "center",
            width: "auto",
            flex: 1,
            justifyContent: "flex-end",
          }}
        >
          <Box
            id="header-nav"
            component="nav"
            aria-label="Main navigation"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: isMobile ? 1.5 : 2.5,
            }}
          >
            <Typography
              component="button"
              onClick={() => props.tierListFunctions.displayPopup("feedbackPopup")}
              sx={{
                cursor: "pointer",
                background: "none",
                border: "none",
                color: "var(--text-secondary)",
                fontWeight: 600,
                letterSpacing: 0.2,
                padding: 0,
                textTransform: "none",
                transition: "color 0.2s ease, transform 0.2s ease",
                "&:hover": {
                  color: "var(--text-primary)",
                  transform: "translateY(-1px)",
                },
              }}
            >
              {t("common.feedback")}
            </Typography>

            <Typography
              component="a"
              href="/tier-list/albums"
              sx={{
                color: "var(--text-secondary)",
                fontWeight: 600,
                letterSpacing: 0.2,
                textDecoration: "none",
                textTransform: "none",
                transition: "color 0.2s ease, transform 0.2s ease",
                "&:hover": {
                  color: "var(--text-primary)",
                  transform: "translateY(-1px)",
                },
              }}
            >
              {t("navigation.tierList")}
            </Typography>

            <Typography
              component="a"
              href="/Grid"
              sx={{
                color: "var(--text-secondary)",
                fontWeight: 600,
                letterSpacing: 0.2,
                textDecoration: "none",
                textTransform: "none",
                transition: "color 0.2s ease, transform 0.2s ease",
                "&:hover": {
                  color: "var(--text-primary)",
                  transform: "translateY(-1px)",
                },
              }}
            >
              {t("navigation.grid")}
            </Typography>


          </Box>
          <Tooltip title={mode === "light" ? t("settings.switchToDark") : t("settings.switchToLight")}>
            <IconButton
              onClick={toggleMode}
              aria-label={mode === "light" ? t("settings.switchToDark") : t("settings.switchToLight")}
              size="small"
              sx={{
                color: "var(--text-secondary)",
                border: "1px solid var(--border-soft)",
                "&:hover": { color: "var(--text-primary)", backgroundColor: "var(--hover-overlay)" },
              }}
            >
              {mode === "light" ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          {props.state.isAuthenticated && props.state.user && (
            <Notifications {...props} />
          )}
          {props.state.isAuthenticated && props.state.user ? (
            <>
              <Tooltip title={props.state.privacyMode ? (props.state.user.name?.split(" ")[0] || "User") : (props.state.user.name || props.state.user.email)}>
                <Avatar
                  onClick={handleAvatarClick}
                  src={props.state.userProfileInfo?.profile_picture || undefined}
                  sx={{ width: 32, height: 32, cursor: "pointer", bgcolor: "var(--accent)", color: "var(--on-accent)" }}
                >
                  {(props.state.user.name || "U")[0].toUpperCase()}
                </Avatar>
              </Tooltip>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{ paper: { sx: { mt: 1, minWidth: 160 } } }}
              >
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    window.location.href = `/profile/${props.state.user.aliasName || props.state.user.id}`;
                  }}
                >
                  <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>{t("navigation.myProfile") || "My Profile"}</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    props.mainFunctions.handleSignOut();
                  }}
                >
                  <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>{t("settings.signOut") || "Sign Out"}</ListItemText>
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Tooltip title="Sign in">
              <Box
                onClick={() => props.basicFunctions.updateState({ showSignInPrompt: true })}
                sx={{
                  width: 32,
                  height: 32,
                  cursor: "pointer",
                  borderRadius: "50%",
                  // Stays white in both modes — it holds the Google "G" mark.
                  backgroundColor: "#ffffff",
                  border: "1px solid var(--border-soft)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  "&:hover": { opacity: 0.85 },
                }}
              >
                <svg width="18" height="18" viewBox="0 0 48 48">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                  <path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.01 24.01 0 0 0 0 21.56l7.98-6.19z"/>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                </svg>
              </Box>
            </Tooltip>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
}
export default Header;
